import { useRef, useState } from "react";
import { faPlusCircle, faTimesCircle } from "@fortawesome/pro-solid-svg-icons";
import { useRouter } from "next/navigation";

import Button from "@/components/button";
import { TextInput } from "@/components/forms";
import { ButtonBar, Module } from "@/components/layouts";
import UserError from "@/components/user-error";
import { AnimationType, PixelMode } from "@/gql/generated/graphql";
import { useModal } from "@/hooks/use-modal";
import * as editorActions from "@/lib/state/actions/editor-actions";
import { useAppDispatch } from "@/lib/state/store";

import { ButtonGroup } from "../../layouts/button-group";

import ModalTitle from "./modal-title";

const ChooseAnimationDimensionsModal = () => {
  const { closeModal } = useModal();
  const dispatch = useAppDispatch();
  const router = useRouter();

  const [error, setError] = useState<string[]>([]);
  const widthRef = useRef<HTMLInputElement>(null);
  const heightRef = useRef<HTMLInputElement>(null);

  const onCancelClicked = () => {
    closeModal();
  };

  const onCreateClicked = () => {
    const width = parseInt(widthRef?.current?.value ?? "0");
    const height = parseInt(heightRef?.current?.value ?? "0");

    if (
      !width ||
      !height ||
      width < 16 ||
      width > 1000 ||
      height < 16 ||
      height > 1000
    ) {
      return setError(["Width and height must be between 16 and 1000"]);
    }

    if (width % 2 || height % 2) {
      return setError(["Width and height must be even numbers"]);
    }

    dispatch(
      editorActions.createNew({
        width,
        height,
        pixelMode: PixelMode.Standard,
        type: AnimationType.Standalone,
      }),
    );
    router.push("/editor");
    closeModal();
  };

  const onItemSelected = (
    width: string,
    height: string,
    isPixelArt = false,
  ) => {
    if (!widthRef?.current?.value || !heightRef?.current?.value) return;
    widthRef.current.value = width;
    heightRef.current.value = height;
  };

  return (
    <div>
      <ModalTitle>Create New Animation</ModalTitle>
      <Module className="text-center">
        <p>
          Please choose the size for your new animation:
          <br />
          (max 1000, min 16)
        </p>
        {error && <UserError errors={error} />}
      </Module>
      <Module className="text-center">
        <Button onClick={() => onItemSelected("400", "300")}>400x300</Button>{" "}
        <Button onClick={() => onItemSelected("800", "600")}>800x600</Button>{" "}
        <Button onClick={() => onItemSelected("768", "480")}>768x480</Button>{" "}
      </Module>

      <Module className="text-center">
        <p>Widths and heights must be even numbers</p>
        <div>
          <TextInput
            id="width"
            label="width"
            ref={widthRef}
            inline
            className="w-20"
            maxLength={4}
            defaultValue="400"
            type="number"
            max={1000}
            min="16"
            step="2"
          />
          <TextInput
            id="height"
            label="height"
            ref={heightRef}
            className="w-20"
            inline
            maxLength={4}
            defaultValue="300"
            type="number"
            max={1000}
            min="16"
            step="2"
          />
        </div>
      </Module>
      <ButtonBar>
        <ButtonGroup align="right">
          <Button
            icon={faTimesCircle}
            onClick={onCancelClicked}
            type="negative"
          >
            Cancel
          </Button>
          <Button
            icon={faPlusCircle}
            onClick={onCreateClicked}
            type="positive"
          >
            Create New Animation
          </Button>
        </ButtonGroup>
      </ButtonBar>
    </div>
  );
};

export default ChooseAnimationDimensionsModal;
